import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, contentChildren, DOCUMENT, ElementRef, inject, input, output, Signal, ChangeDetectionStrategy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TreeTableNode } from 'primeng/api';
import { TreeTable } from 'primeng/treetable';
import { HisCoreSharedModule } from '../../../core/his-core-shared.module';
import { and, not, resolveOptionalValue, resolveValue } from '../../../core/utils';
import { BooleanResolver, Supplier } from '../../../core/utils/his-type.utils';
import { HisTreeNode, HisTreeNodeData } from '../../model/his-tree.model';
import { HisTreeActionTemplateDirective } from './his-tree-action-buttons.directive';
import {
    ActionRenderModel, canCut, canPaste, DefaultActionBaseOverride, DefaultActionId, DefaultActionOverride,
    DefaultGroupKey, hasChildren, isCopySource, isCutMode, isCutSource, isExpanded, isFirstNode, isLastNode,
    ResolvedActionGroup, TreeActionBaseDef, TreeActionButtonsConfig, TreeActionContext, TreeActionDef,
    TreeActionEvent, TreeMultiActionDef, TreeSingleActionDef
} from './his-tree-action-buttons.model';
import { HisTreeCopyCutDirective } from './his-tree-copy-cut.directive';

const DEFAULT_ACTION_DEFS: Record<DefaultGroupKey, TreeActionDef[]> = {
    editView: [
        { id: 'edit', icon: 'PENCIL',    title: 'common.editOf' },
        { id: 'view', icon: 'MAGNIFIER', title: 'view.images.magnifier_detail' }
    ],
    copyCut: [
        { slot: 'copyPaste', actions: [
            { id: 'copy',       icon: 'COPY',       title: 'view.images.copy_paste', dependencyScope: 'shared', visible: not( isCopySource ) },
            { id: 'cancelCopy', icon: 'STOP_MINUS', title: 'common.cancel',          dependencyScope: 'shared', visible: isCopySource }
        ] },
        { slot: 'cutPaste', actions: [
            { id: 'cut',       icon: 'CUT',        title: 'view.controls.cut', dependencyScope: 'shared', visible: and( not( isCutMode ), canCut ) },
            { id: 'cancelCut', icon: 'STOP_MINUS', title: 'common.cancel',     dependencyScope: 'shared', visible: and( isCutMode, isCutSource ) },
            {
                id: 'paste', icon: 'ARROW_DOWN_FAT', title: 'view.controls.paste', dependencyScope: 'shared', visible: and( isCutMode, not( isCutSource ), canPaste ),
                focusTargetRoot: 'host', implicitRestoreFocus: false, focusTarget: root => root.closest( 'tr' )?.querySelector('button.p-treetable-toggler')
            }
        ] }
    ],
    expandCollapse: [{
        slot: 'expandCollapse', actions: [
            { id: 'expandAll',   icon: 'TREE_INCREASE', title: 'de.his.faces.tags.tree.button.expandAll',   visible: and( not( isExpanded ), hasChildren ) },
            { id: 'collapseAll', icon: 'TREE_DECREASE', title: 'de.his.faces.tags.tree.button.collapseAll', visible: and( isExpanded, hasChildren ) }
        ]
    }],
    addActions: [
        { id: 'addChild', icon: 'NEW_LEVEL_BELOW', title: 'image.alt.new_level_below' },
        { id: 'addAbove', icon: 'NEW_ROW_ABOVE',   title: 'image.alt.new_row_above' },
        { id: 'addBelow', icon: 'NEW_ROW_BELOW',   title: 'image.alt.new_row_below' }
    ],
    moveActions: [
        { id: 'moveUp',   icon: 'ARROW_UP',   title: 'view.images.arrow_up',   visible: not( isFirstNode ) },
        { id: 'moveDown', icon: 'ARROW_DOWN', title: 'view.images.arrow_down', visible: not( isLastNode ) }
    ],
    delete: [
        { id: 'delete', icon: 'DELETE', title: 'common.deleteOf' }
    ]
};

const DEFAULT_ACTIONS_FLAT = new Set(
    Object.values(DEFAULT_ACTION_DEFS).flatMap(defs =>
        defs.flatMap(def => 'slot' in def ? def.actions.map(action => action.id) : [def.id])
    )
);

/** Default groups rendered before the single fixed middle insertion slot */
const GROUPS_BEFORE_CUSTOM: DefaultGroupKey[] = ['editView', 'copyCut'];
/** Default groups rendered after the single fixed middle insertion slot */
const GROUPS_AFTER_CUSTOM: DefaultGroupKey[] = ['expandCollapse', 'addActions', 'moveActions', 'delete'];

function buildCssClass( baseClasses: string[], extra: string | string[] | undefined ): string {
    const extraCss = typeof extra === 'string' ? [ extra ] : extra ?? [];
    return [ ...baseClasses, ...extraCss ].join( ' ' );
}

function slotId( def: { slot?: string, id?: string } ): string {
    return 'slot' in def ? def.slot : def.id;
}

@Component( {
    selector: 'his-tree-action-buttons',
    imports: [NgTemplateOutlet, HisCoreSharedModule, RouterModule],
    templateUrl: './his-tree-action-buttons.component.html',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    host: { '[attr.data-uid]': 'node().uid' },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
} )
export class HisTreeActionButtonsComponent<T extends HisTreeNodeData = HisTreeNodeData> {

    private readonly document = inject( DOCUMENT );
    private readonly hostElRef = inject(ElementRef, { self: true });
    private readonly treeTable = inject( TreeTable );
    /** The tree-table row node for which to render action buttons */
    readonly rowNode = input.required<TreeTableNode<T>>();
    /** Field name used as the display label for this node */
    readonly labelField = input<string>();
    /** Full configuration for default action overrides and custom actions */
    readonly config = input<TreeActionButtonsConfig>();

    readonly edit        = output<TreeActionEvent<T>>();
    readonly view        = output<TreeActionEvent<T>>();
    readonly copyAction  = output<TreeActionEvent<T>>();
    readonly cutAction   = output<TreeActionEvent<T>>();
    readonly pasteAction = output<TreeActionEvent<T>>();
    readonly cancelCut   = output<TreeActionEvent<T>>();
    readonly cancelCopy  = output<TreeActionEvent<T>>();
    readonly expandAll   = output<TreeActionEvent<T>>();
    readonly collapseAll = output<TreeActionEvent<T>>();
    readonly addAbove    = output<TreeActionEvent<T>>();
    readonly addBelow    = output<TreeActionEvent<T>>();
    readonly addChild    = output<TreeActionEvent<T>>();
    readonly moveUp      = output<TreeActionEvent<T>>();
    readonly moveDown    = output<TreeActionEvent<T>>();
    readonly delete      = output<TreeActionEvent<T>>();

    /** Emitted for every callback-mode action click, both default and custom. */
    readonly actionTriggered = output<TreeActionEvent<T>>();

    /** List of content children directives for per-action template overrides. */
    private readonly templateOverrideList = contentChildren( HisTreeActionTemplateDirective );
    /** Map of action IDs to their corresponding template overrides. */
    protected readonly templateOverrideMap = computed( () => 
        new Map( this.templateOverrideList().filter( d => !d.replaceEntireTemplate ).map( d => [d.hisTreeActionTemplate(), d.templateRef] ) ),
    );
    protected readonly entireTemplateOverrideMap = computed( () =>
        new Map( this.templateOverrideList().filter( d => d.replaceEntireTemplate ).map( d => [d.hisTreeActionTemplate(), d.templateRef] ) ),
    );

    protected readonly node = computed( () => this.rowNode().node as HisTreeNode<T> );
    private readonly rowData = computed( () => this.node().data );
    /** Computed signal indicating whether this is the first instance of action-buttons at the top level of the tree. */
    private readonly firstInstance = computed( () => this.rowNode().level === 0 && this.node().index === 0 );

    private readonly cutModeDirective = inject( HisTreeCopyCutDirective, { optional: true } );
    private readonly sharedDependencySignals = new Map<string, Signal<boolean>>();

    /** Map of action IDs to their corresponding output emitters. */
    private readonly actionOutputs: Partial<Record<DefaultActionId, { emit: ( e: TreeActionEvent<T> ) => void }>> = {
        edit:        this.edit,
        view:        this.view,
        copy:        this.copyAction,
        cut:         this.cutAction,
        paste:       this.pasteAction,
        cancelCut:   this.cancelCut,
        cancelCopy:  this.cancelCopy,
        expandAll:   this.expandAll,
        collapseAll: this.collapseAll,
        addAbove:    this.addAbove,
        addBelow:    this.addBelow,
        addChild:    this.addChild,
        moveUp:      this.moveUp,
        moveDown:    this.moveDown,
        delete:      this.delete,
    };

    /** Computed signal for the resolved action groups. */
    protected readonly resolvedGroups = computed<ResolvedActionGroup[]>( () => {
        const ctx = this.buildContext();
        const { cfg, defaults } = this.configLayout();

        const slotKeys = new Set<string>();
        const resolvedGroupsBefore = GROUPS_BEFORE_CUSTOM.map( key => this.buildDefaultGroup( key, defaults, cfg, ctx, slotKeys ) ).filter( g => g.actions.length > 0 );
        const resolvedGroupsAfter = GROUPS_AFTER_CUSTOM.map( key => this.buildDefaultGroup( key, defaults, cfg, ctx, slotKeys ) ).filter( g => g.actions.length > 0 );

        const groupKeys = new Set<string>( [...resolvedGroupsBefore, ...resolvedGroupsAfter].map( g => g.key ) );
        const groups: ResolvedActionGroup[] = [
            ...resolvedGroupsBefore,
            ...( cfg.customGroups ?? [] ).map( ( g, i ) => {
                const groupKey = !!g.key && !groupKeys.has( g.key ) ? g.key : `custom_${i}`;
                groupKeys.add( groupKey );
                return {
                    key: groupKey,
                    actions: this.resolveCustomActions( g.slots, groupKey, ctx, slotKeys )
                };
            } ),
            ...resolvedGroupsAfter,
        ];

        const groupsWithActions = groups.filter( g => g.actions.length > 0 );
        return groupsWithActions;
    } );

    /** Builds the context for the current tree action, including row data, node, label, and custom metadata. */
    private readonly buildContext = computed<TreeActionContext<T>>( () => {
        const rowData = this.rowData() ?? ( {} as T );
        const node = this.node();
        const label = rowData[this.labelField()] || rowData.label || rowData.title || rowData.defaulttext || node.label;
        return { rowData, node, label, customMeta: { copyCutMode: this.cutModeDirective } };
    } );

    /** Computes the configuration layout, including defaults and custom group settings. */
    private readonly configLayout = computed( () => {
        const cfg = this.config() ?? {};
        const defaults: Record<string, DefaultActionOverride> = {};
        for ( const def of cfg.defaults ?? [] ) {
            if ( typeof def === 'string' && DEFAULT_ACTIONS_FLAT.has(def) ) {
                defaults[def] = { id: def } as any;
            } else if ( typeof def === 'string' ) {
                // if not in the flat action map, then its a slot id
                defaults[def] = { slot: def } as any;
            } else {
                defaults[slotId(def)] = def;
            }
        }
        return {cfg, defaults};
    } );

    /**
     * Handles the click event for any action.
     * Exposed as a public method so that custom templates may also invoke the default actionTrigger behavior.
     * @param action The action that was clicked.
     * @param event The original browser event.
     */
    public onActionClick( action: ActionRenderModel, event: Event ): void {
        const payload: TreeActionEvent<T> = {
            actionId: action.identity.id,
            groupKey: action.identity.groupKey,
            nodeLabel: action.presentation.label,
            rowData: this.rowData()!,
            node: this.node()!,
            originalEvent: event,
        };

        // Per-action output for built-in defaults
        this.actionOutputs[action.identity.id as DefaultActionId]?.emit( payload );
        this.handleImplicitCutActions( payload );

        // Unified output for all actions
        this.actionTriggered.emit( payload );

        this.handleRestoreFocus( action );        
    }

    // --- Group and action resolvers ----

    private buildDefaultGroup(
        groupKey: DefaultGroupKey,
        defaults: Record<string, DefaultActionOverride>,
        cfg: TreeActionButtonsConfig,
        ctx: TreeActionContext,
        slotKeys: Set<string>,
    ): ResolvedActionGroup {
        const appended = cfg.appendToGroup?.[groupKey] ?? [];
        return {
            key: groupKey,
            actions: [
                ...DEFAULT_ACTION_DEFS[groupKey].filter( def => slotId( def ) in defaults ).map( def =>
                    this.resolveDefaultAction( def, defaults[ slotId(def) ], groupKey, ctx ),
                ).filter( action => this.filterDuplicateSlots( action, slotKeys ) ),
                ...this.resolveCustomActions( appended, groupKey, ctx, slotKeys ),
            ],
        };
    }

    private resolveDefaultAction(
        defaultAction: TreeActionDef,
        override: DefaultActionOverride | undefined,
        groupKey: DefaultGroupKey,
        ctx: TreeActionContext,
    ): ActionRenderModel {
        return this.doResolveAction( ctx, this.mergeDefaultActionOverrides( defaultAction, override ), groupKey, true );
    }

    private mergeDefaultActionOverrides(
        defaultAction: TreeActionDef,
        override: DefaultActionOverride | undefined,
    ): TreeActionDef {
        if ( !override ) {
            return defaultAction;
        }
        if ( 'slot' in override && 'slot' in defaultAction ) {
            return {
                ...defaultAction,
                actions: defaultAction.actions.map( action => {
                    const overrideAction = override.actions?.find( a => a.id === action.id );
                    return overrideAction ? this.mergeDefaultActionBaseOverrides( action, overrideAction ) : action;
                })
            };
        }
        if (!('slot' in override || 'slot' in defaultAction)) {
            const merged = this.mergeDefaultActionBaseOverrides( defaultAction, override );
            return merged;
        }
        // should not happen
        console.warn('Incompatible default action override:', override, 'for default action:', defaultAction);
        return defaultAction;
    }

    private mergeDefaultActionBaseOverrides(
        defaultAction: TreeActionBaseDef,
        override: DefaultActionBaseOverride,
    ): TreeActionBaseDef {
        return {
            ...defaultAction,
            visible: override.visible ?? defaultAction.visible,
            disabled: override.disabled ?? defaultAction.disabled,
            routeAction: override.routeAction ?? defaultAction.routeAction,
            cssClass: override.cssClass ?? defaultAction.cssClass,
            icon: override.icon ?? defaultAction.icon,
            title: override.title ?? defaultAction.title,
            focusTarget: override.focusTarget ?? defaultAction.focusTarget,
            implicitRestoreFocus: override.implicitRestoreFocus ?? defaultAction.implicitRestoreFocus,
        };
    }

    private resolveCustomActions(
        defs: TreeActionDef[],
        groupKey: string,
        ctx: TreeActionContext,
        slotKeys: Set<string>
    ): ActionRenderModel[] {
        return defs.map( def => this.doResolveAction( ctx, def, groupKey, false ) ).filter( action => this.filterDuplicateSlots( action, slotKeys ) );
    }

    private filterDuplicateSlots( action: ActionRenderModel, slotKeys: Set<string> ): boolean {
        const slot = action.identity.slotId;
        if ( slotKeys.has( slot ) ) {
            if (this.firstInstance()) {
                console.warn( `Duplicate slot removed: '${slot}'`, action.identity );
            }
            return false;
        }
        slotKeys.add( slot );
        return true;
    }

    private doResolveAction( ctx: TreeActionContext, def: TreeActionDef, groupKey: string, isDefault: boolean ): ActionRenderModel {
        if ( this.isMultiActionDef( def ) ) {
            return this.reduceSlotActions( def.actions.map( actionDef => this.doResolveDistinctAction( ctx, actionDef, groupKey, def.slot, def.rendered, isDefault ) ) );
        }
        const singlAction = def as TreeSingleActionDef;
        return this.doResolveDistinctAction( ctx, singlAction, groupKey, singlAction.id, singlAction.rendered, isDefault );
    }

    private doResolveDistinctAction( ctx: TreeActionContext, def: TreeActionBaseDef, groupKey: string, slot: string, slotRendered: BooleanResolver<TreeActionContext>, isDefault: boolean = false ): ActionRenderModel {
        const { id, icon, title, dependencyScope = 'row', implicitRestoreFocus = true, focusTarget, focusTargetRoot = 'document' } = def;
        const visible = this.booleanState( id, 'visible', () => resolveValue( def.visible, true, ctx ), dependencyScope );
        const disabled = this.booleanState( id, 'disabled', () => resolveValue( def.disabled, false, ctx ), dependencyScope );
        const rendered = this.booleanState( id, 'rendered', () => resolveValue( slotRendered, true, ctx ), dependencyScope );
        const routeAction = resolveOptionalValue( def.routeAction, ctx );
        const cssClass = buildCssClass( routeAction ? ['treeAction'] : ['treeAction', 'icon'], def.cssClass );
        const elementUid = ctx.node.uid + '.' + slot;
        return {
            identity: { id, slotId: slot, groupKey, elementUid, isDefault },
            state: { visible, disabled, rendered },
            presentation: { cssClass, icon, title, label: ctx.label },
            focus: { restoreSelf: implicitRestoreFocus, target: focusTarget, targetRoot: focusTargetRoot },
            routeAction
        };
    }

    private reduceSlotActions( actions: ActionRenderModel[] ): ActionRenderModel {
        // Keep only the first visible action per slot
        const visibleActions = actions.filter( action => action.state.visible() );
        if ( visibleActions.length === 0 ) {
            return this.createSpacerDummy( actions[0] );
        }

        if ( visibleActions.length > 1 ) {
            console.debug( `Multiple visible actions found for slot "${visibleActions[0].identity.slotId}":`, visibleActions );
        }
        return visibleActions[0];
    }

    private createSpacerDummy( action: ActionRenderModel ): ActionRenderModel {
        return {
            identity: {
                id: 'spacer',
                slotId: action.identity.slotId,
                groupKey: action.identity.groupKey,
                elementUid: 'spacer',
                isDefault: true
            },
            state: {
                visible: () => false,
                disabled: () => true,
                rendered: action.state.rendered // retain the original render state
            },
            presentation: {
                cssClass: 'treeAction icon',
                icon: '',
                title: '',
                label: undefined
            },
            focus: {
                restoreSelf: false,
                target: undefined,
                targetRoot: 'document'
            }
        };
    }

    /**
     * Creates a boolean state supplier from the value resolver. Regular conditions are evaluated immediately and returned as a constant supplier.\
     * Shared-dependency conditions are wrapped in a computed signal, which is returned as the supplier.
     * This allows for complex shared-dependency conditions (e.g. the 'cutMode' flag) to be evaluated without triggering re-computation of the entire state.
     */
    private booleanState( actionId: string, actionProp: string, resolver: Supplier<boolean>, dependencyScope: 'row' | 'shared' = 'row' ): Supplier<boolean> {
        if ( dependencyScope === 'row' ) {
            const value = resolver();
            return () => value;
        }
        const key = `${actionId}_${actionProp}`;
        if ( !this.sharedDependencySignals.has( key ) ) {
            const signal = computed( resolver );
            this.sharedDependencySignals.set( key, signal );
        }
        return this.sharedDependencySignals.get( key )!;
    }

    /** Performs the implicit cut, copy, paste, and cancelCut actions based on the payload using the TreeCopyCutDirective */
    private handleImplicitCutActions( payload: TreeActionEvent ): void {
        switch ( payload.actionId ) {
            case 'cut': return this.cutModeDirective?.cut( payload.node );
            case 'copy': return this.cutModeDirective?.copy( payload.node );
            case 'paste': return this.cutModeDirective?.paste( payload.node );
            case 'cancelCut': return this.cutModeDirective?.cancelCutMode();
            case 'cancelCopy': return this.cutModeDirective?.cancelCopyMode();
        }
    }

    private isMultiActionDef<A extends HisTreeNodeData = HisTreeNodeData>( def: TreeActionDef<A> ): def is TreeMultiActionDef<A> {
        return 'slot' in def && Array.isArray( def.actions );
    }
    

    /** Restores focus to the appropriate element after an action is triggered, based on the action's focus configuration. */
    private handleRestoreFocus( action: ActionRenderModel ): void {
        if ( action.focus.restoreSelf || !!action.focus.target ) {
            setTimeout( () => {
                if ( action.focus.restoreSelf ) {
                    // if restoreSelf is true, it takes priority
                    // first try to focus the action itself
                    const self = this.treeTable.el.nativeElement.querySelector( `[data-uid="${action.identity.elementUid}"]` );
                    if ( this.isFocusable( self ) ) {
                        self.focus();
                        return;
                    }

                    // the next best focus target is the nearest element within the same group
                    const target = this.findNearestOfGroup( action.identity.groupKey );
                    if ( this.isFocusable( target ) ) {
                        target.focus();
                        return;
                    }
                }

                // finally, try to focus the explicitly specified target if available
                if ( action.focus.target ) {
                    const targetRoot = this.getFocusTargetRoot( action.focus.targetRoot );
                    const target = this.getFocusTarget( action.focus.target, targetRoot );
                    if ( this.isFocusable( target ) ) {
                        target.focus();
                        return;
                    }
                }
            } );
        }
    }

    private getFocusTarget( target: ActionRenderModel['focus']['target'], targetRoot: HTMLElement ): HTMLElement | null {
        if ( typeof target === 'string' ) {
            return targetRoot.querySelector( target ) as HTMLElement | null;
        }

        if ( typeof target === 'function' ) {
            return target( targetRoot );
        }

        return null;
    }

    private findNearestOfGroup( groupKey: string ): HTMLElement | null {
        const buttonsEl = this.getFocusTargetRoot( 'host' );
        const groupSelector = `[data-group="${groupKey}"]`;
        return buttonsEl?.querySelector( groupSelector + '>a[href], ' + groupSelector + '>button:not([disabled])' ) as HTMLElement | null;
    }

    private getFocusTargetRoot( targetRoot: ActionRenderModel['focus']['targetRoot'] ): HTMLElement | null {
        switch ( targetRoot ) {
            case 'document': return this.document.documentElement;
            case 'table': return this.treeTable.el.nativeElement;
            case 'host': return this.document.querySelector( `his-tree-action-buttons[data-uid="${this.node().uid}"]` );
        }
    }

    private isFocusable<EL>( el: EL ): el is EL & { focus: () => void } {
        return !!el && typeof (el as any).focus === 'function';
    }
}